/**
 * AjustesUserV2 — Employee settings page (V2 redesign).
 *
 * Horizontal pill bar navigation with sections:
 * 1. Datos personales — Phone, personal email, profession
 * 2. Seguridad — Password change
 * 3. Retribución flexible — Flex benefits config (placeholder)
 * 4. Integraciones — Indexa Capital connection
 * 5. Notificaciones — Preferences
 * 6. Documentos — Tax certificates, plan docs
 * 7. Zona de peligro — Plan cancellation
 */
import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { PageHeader } from '../../../components/ui/page-header';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '../../../components/ui/select';
import { Separator } from '../../../components/ui/separator';
import BorrarCuenta from '../../../movil/BorrarCuenta';
import EnlacePrivacidad from '../../../movil/EnlacePrivacidad';
import { esApp } from '../../../movil/plataforma';
import InterruptorBiometria from '../../../movil/InterruptorBiometria';
import {
	Eye,
	EyeOff,
	CheckCircle2,
	User,
	Shield,
	Coins,
	Link2,
	Bell,
	FileText,
	AlertTriangle,
	Download,
	ExternalLink,
	Loader2,
	HeadphonesIcon,
	Mail,
	ChevronDown
} from 'lucide-react';
import { cn } from '../../../lib/utils';

import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import getCurrentUser from '../../../api/users/getCurrentUser';
import updateUserProfile from '../../../api/users/updateUserProfile';
import updateUserPassword from '../../../api/users/updateUserPassword';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import getIndexaToken from '../../../api/thirdparty/indexa/getIndexaToken';
import { CurrentUserI } from '../../../api/users/interfaces/Users';
import { CompanyEppConfigI } from '../../../api/companies/interfaces/CompanyEppConfig';
import { PROFESSIONAL_ACTIVITIES } from '../../../constants/professionalActivities';
import FeedbackModal from '../../../components/Modals/FeedbackModal';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import updateUserLanguage from '../../../api/users/updateUserLanguage';
import getOptionByName from '../../../api/generic-api/getOptionByName';
import VoluntaryExitDialogV2 from '../../../components/VoluntaryExitDialogV2';
import IndexaCapitalConfigModalV2 from '../../../components/Integrations/IndexaCapitalConfigModalV2';
import { useLocation } from 'react-router-dom';

import { analytics } from '../../../services/analytics';
import { CONTACT_DATA_BANNER_EVENTS } from '../../../components/ContactDataBanner/useContactDataBanner';
import { isDemoAccountBlocked } from '../../../utils/demoAccountInterceptor';
import { esExEmpleado } from '../../../utils/esExEmpleado';

// Section definitions - labels are translation keys, resolved at render time
const SUPPORT_URL = 'https://support.arcadigital.app/';

const SECTION_DEFS = [
	{ id: 'datos-personales', labelKey: 'settings.sections.profile', icon: User },
	{ id: 'seguridad', labelKey: 'settings.sections.security', icon: Shield },
	{ id: 'retribucion-flexible', labelKey: 'settings.sections.compensation', icon: Coins },
	{ id: 'integraciones', labelKey: 'settings.sections.integrations', icon: Link2 },
	{ id: 'notificaciones', labelKey: 'settings.sections.notifications', icon: Bell },
	{ id: 'documentos', labelKey: 'settings.sections.documents', icon: FileText },
	{ id: 'soporte', labelKey: 'user.settings.supportTitle', icon: HeadphonesIcon },
	{ id: 'zona-peligro', labelKey: 'settings.sections.withdrawal', icon: AlertTriangle }
] as const;

interface AjustesUserV2Props {
	isIndividual?: boolean;
}

// Section Nav Component
const SectionNav: React.FC<{
	activeSection: string;
	onClickSection: (id: string) => void;
	translate: (key: string) => string;
	/** Sin la sección «Zona de peligro» (ex-empleado en la web, product#87). */
	sinZonaPeligro: boolean;
	/** La sección solo guarda «Borrar mi cuenta» (ex-empleado en la app): se llama «Tu cuenta». */
	zonaPeligroComoCuenta: boolean;
}> = ({ activeSection, onClickSection, translate, sinZonaPeligro, zonaPeligroComoCuenta }) => {
	const activeRef = useRef<HTMLButtonElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (activeRef.current && scrollContainerRef.current) {
			const container = scrollContainerRef.current;
			const pill = activeRef.current;
			const pillLeft = pill.offsetLeft;
			const pillWidth = pill.offsetWidth;
			const containerWidth = container.offsetWidth;
			const scrollLeft = container.scrollLeft;

			if (pillLeft < scrollLeft + 16) {
				container.scrollTo({ left: Math.max(0, pillLeft - 16), behavior: 'smooth' });
			} else if (pillLeft + pillWidth > scrollLeft + containerWidth - 16) {
				container.scrollTo({
					left: pillLeft + pillWidth - containerWidth + 16,
					behavior: 'smooth'
				});
			}
		}
	}, [activeSection]);

	return (
		<div className="sticky top-0 z-30 bg-background border-b border-border py-3 mb-6">
			<div ref={scrollContainerRef} className="flex gap-1.5 overflow-x-auto scrollbar-hide">
				{SECTION_DEFS.filter((s) => !(sinZonaPeligro && s.id === 'zona-peligro')).map((s) => {
					const comoCuenta = zonaPeligroComoCuenta && s.id === 'zona-peligro';
					const Icon = comoCuenta ? User : s.icon;
					const isActive = activeSection === s.id;
					const isDanger = s.id === 'zona-peligro' && !comoCuenta;
					return (
						<button
							key={s.id}
							ref={isActive ? activeRef : null}
							onClick={() => onClickSection(s.id)}
							className={cn(
								'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 shrink-0',
								isActive && !isDanger && 'bg-primary/10 text-primary',
								isActive && isDanger && 'bg-destructive/10 text-destructive',
								!isActive && 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
							)}
						>
							<Icon className="w-3.5 h-3.5" />
							{translate(comoCuenta ? 'settings.yourAccount' : s.labelKey)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

// Section Header Component
const SectionHeader: React.FC<{
	icon: React.ElementType;
	title: string;
	description?: string;
}> = ({ icon: Icon, title, description }) => (
	<div className="flex items-start gap-3 mb-5">
		<div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
			<Icon className="w-5 h-5 text-primary" />
		</div>
		<div>
			<h2 className="text-lg font-semibold text-foreground">{title}</h2>
			{description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
		</div>
	</div>
);

const AjustesUserV2: React.FC<AjustesUserV2Props> = ({ isIndividual = false }) => {
	const {
		state: { translate },
		dispatch
	} = useContext(I18nContext);
	const { token, userId, language, setLanguage } = useContext(ArcaContext);
	const location = useLocation();

	// Loading state
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	// User data
	const [currentUser, setCurrentUser] = useState<CurrentUserI | null>(null);

	// Form state - Profile
	const [phone, setPhone] = useState('');
	const [personalEmail, setPersonalEmail] = useState('');
	const [profession, setProfession] = useState('');
	// Valores iniciales (los que vienen del back). Si tenían valor, el front no
	// permite borrarlos: el back rechaza "" en phone (regex) y personalEmail
	// (@IsEmail), y la rama de borrado del service nunca se alcanza.
	const [initialPhone, setInitialPhone] = useState('');
	const [initialPersonalEmail, setInitialPersonalEmail] = useState('');

	// Form state - Password
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	// Form state - Notifications
	const [notifContributions, setNotifContributions] = useState(true);
	const [notifDocuments, setNotifDocuments] = useState(true);
	const [notifMarketing, setNotifMarketing] = useState(false);
	const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);

	// Navigation state
	const [activeSection, setActiveSection] = useState<string>(SECTION_DEFS[0].id);
	const isScrollingFromClick = useRef(false);
	const clickTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	// Modal state
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isIndexaModalOpen, setIsIndexaModalOpen] = useState(false);
	const [isIndexaConnected, setIsIndexaConnected] = useState(false);
	const [modalMessage, setModalMessage] = useState('');
	const [modalIsError, setModalIsError] = useState(false);

	// Voluntary exit state
	const [showExitDialog, setShowExitDialog] = useState(false);
	const [noWill, setNoWill] = useState(false);
	const [companyEppConfig, setCompanyEppConfig] = useState<CompanyEppConfigI | null>(null);

	// Fetch user data
	const fetchUserData = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await getCurrentUser(token);
			if (response.success) {
				setCurrentUser(response.data);
				setNoWill(response.data.user?.noWill ?? false);
				const loadedPhone = response.data.user?.phone || '';
				const loadedPersonalEmail = response.data.user?.personalEmail || '';
				setPhone(loadedPhone);
				setPersonalEmail(loadedPersonalEmail);
				setInitialPhone(loadedPhone);
				setInitialPersonalEmail(loadedPersonalEmail);

				// Map profession
				const savedActivity = response.data.user?.professionalActivity || '';
				if (savedActivity) {
					const match = PROFESSIONAL_ACTIVITIES.find(
						(a) => a.label.trim() === savedActivity.trim()
					);
					setProfession(match?.value || '');
				}

				// Fetch available languages
				try {
					const langResponse = await getOptionByName(token, 'user-available-languages');
					if (langResponse.data?.value) {
						setAvailableLanguages(JSON.parse(langResponse.data.value));
					}
				} catch (err) {
					console.error('Error fetching available languages:', err);
				}

				// Fetch company EPP config for voluntary exit dialog
				if (response.data.user?.companyId) {
					try {
						const companyInfo = await getCompanyInfoEppConfig(token, response.data.user.companyId);
						setCompanyEppConfig(
							companyInfo?.data?.allCompanies?.[0]?.companyEppConfig as CompanyEppConfigI
						);
					} catch (err) {
						console.error('Error fetching company EPP config:', err);
					}
				}
			}
		} catch (error) {
			console.error('Error fetching user data:', error);
		} finally {
			setIsLoading(false);
		}
	}, [token]);

	useEffect(() => {
		fetchUserData();
	}, [fetchUserData]);

	// Scroll to personal data section when arriving with ?focus=personal-data
	useEffect(() => {
		if (isLoading) return;
		const params = new URLSearchParams(location.search);
		if (params.get('focus') !== 'personal-data') return;
		const el = document.getElementById('datos-personales');
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, [isLoading, location.search]);

	// Check Indexa Capital connection status
	useEffect(() => {
		const checkIndexaStatus = async () => {
			if (!token) return;
			try {
				const response = await getIndexaToken(token);
				setIsIndexaConnected(response.success && !!response.data?.indexaApiKey);
			} catch {
				setIsIndexaConnected(false);
			}
		};
		checkIndexaStatus();
	}, [token]);

	// Intersection observer for section tracking
	useEffect(() => {
		const observers: IntersectionObserver[] = [];
		SECTION_DEFS.forEach((s) => {
			const el = document.getElementById(s.id);
			if (!el) return;
			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting && !isScrollingFromClick.current) {
						setActiveSection(s.id);
					}
				},
				{ rootMargin: '-10% 0px -80% 0px', threshold: 0 }
			);
			observer.observe(el);
			observers.push(observer);
		});
		return () => observers.forEach((o) => o.disconnect());
	}, []);

	const handleClickSection = useCallback((id: string) => {
		setActiveSection(id);
		isScrollingFromClick.current = true;
		clearTimeout(clickTimeoutRef.current);
		clickTimeoutRef.current = setTimeout(() => {
			isScrollingFromClick.current = false;
		}, 1000);
		const el = document.getElementById(id);
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, []);

	const handleSaveProfile = async () => {
		setIsSaving(true);
		let demoAccountBlocked = false;
		try {
			const professionLabel = PROFESSIONAL_ACTIVITIES.find((a) => a.value === profession)?.label;
			const trimmedPhone = phone.trim();
			const trimmedPersonalEmail = personalEmail.trim();

			// El back valida `phone` con regex `^[+\d\s]+$` y `personalEmail` con @IsEmail;
			// ambos rechazan "" con 400. Si el usuario tenía valor y ahora está vacío,
			// no permitimos el borrado desde aquí.
			const phoneCleared = !!initialPhone && !trimmedPhone;
			const personalEmailCleared = !isIndividual && !!initialPersonalEmail && !trimmedPersonalEmail;
			if (phoneCleared || personalEmailCleared) {
				setModalMessage(translate('settings.profileFieldsCannotBeCleared'));
				setModalIsError(true);
				setIsSaving(false);
				setIsModalOpen(true);
				return;
			}

			// Para evitar el 400 del back con strings vacíos, omitimos el campo
			// cuando esté vacío (solo posible si nunca tuvo valor).
			const response = await updateUserProfile(token, {
				...(trimmedPhone ? { phone: trimmedPhone } : {}),
				...(isIndividual || !trimmedPersonalEmail ? {} : { personalEmail: trimmedPersonalEmail }),
				professionalActivity: professionLabel || ''
			});
			if (response.success) {
				const source =
					sessionStorage.getItem(CONTACT_DATA_BANNER_EVENTS.SOURCE_SESSION_KEY) ||
					'settings_direct';
				analytics.track(CONTACT_DATA_BANNER_EVENTS.SAVED, {
					has_phone: !!phone,
					has_personal_email: !!personalEmail,
					source
				});
				sessionStorage.removeItem(CONTACT_DATA_BANNER_EVENTS.SOURCE_SESSION_KEY);
				sessionStorage.removeItem(CONTACT_DATA_BANNER_EVENTS.SHOWN_SESSION_KEY);

				setModalMessage(translate('settings.profileUpdated'));
				setModalIsError(false);
			} else if (isDemoAccountBlocked(response)) {
				// Cuenta demo: ya sale su aviso único (DemoAccountBlockedNotice), sin otro modal.
				demoAccountBlocked = true;
			} else {
				setModalMessage(response.message || translate('settings.profileUpdateError'));
				setModalIsError(true);
			}
		} catch (error) {
			setModalMessage(translate('settings.profileUpdateError'));
			setModalIsError(true);
		} finally {
			setIsSaving(false);
			setIsModalOpen(!demoAccountBlocked);
		}
	};

	const handleUpdatePassword = async () => {
		if (newPassword !== confirmPassword) {
			setModalMessage(translate('settings.passwordsDoNotMatch'));
			setModalIsError(true);
			setIsModalOpen(true);
			return;
		}

		setIsSaving(true);
		let demoAccountBlocked = false;
		try {
			const response = await updateUserPassword(token, userId || '', currentPassword, newPassword);
			if (response.success) {
				setModalMessage(translate('settings.passwordUpdated'));
				setModalIsError(false);
				setCurrentPassword('');
				setNewPassword('');
				setConfirmPassword('');
			} else if (isDemoAccountBlocked(response)) {
				// Cuenta demo: ya sale su aviso único (DemoAccountBlockedNotice), sin otro modal.
				demoAccountBlocked = true;
			} else {
				setModalMessage(response.message || translate('settings.passwordUpdateError'));
				setModalIsError(true);
			}
		} catch (error) {
			setModalMessage(translate('settings.passwordUpdateError'));
			setModalIsError(true);
		} finally {
			setIsSaving(false);
			setIsModalOpen(!demoAccountBlocked);
		}
	};

	const changeLanguage = async (newLanguage: string) => {
		if (newLanguage === language) return;
		const prevLanguage = language;
		setLanguage(newLanguage);
		dispatch({ type: 'setLanguage', payload: newLanguage });
		localStorage.setItem('language', newLanguage);
		try {
			await updateUserLanguage(token, newLanguage);
			analytics.setLanguage(newLanguage);
		} catch (error) {
			console.error('Error updating language:', error);
			setLanguage(prevLanguage);
			dispatch({ type: 'setLanguage', payload: prevLanguage });
			localStorage.setItem('language', prevLanguage);
			setModalMessage(translate('user.userSettings.messageError'));
			setModalIsError(true);
			setIsModalOpen(true);
		}
	};

	// product#87: la empresa le dio de baja, así que «Salir del plan» ya no tiene sentido. En la
	// app la sección se queda, porque dentro vive «Borrar mi cuenta», y se llama «Tu cuenta».
	// Ajustes no mira el rescate: quien rescata y además es TERMINATED tampoco lo ve, y está
	// bien, porque no tiene sentido para quien rescata (revisión total, Marisa 01/11).
	const exEmpleado = esExEmpleado(currentUser);
	const conZonaPeligro = !exEmpleado || esApp();

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="max-w-2xl mx-auto pb-12">
			{/* 🔴 Cabecera nueva: esta pantalla NO tenía `<h1>` ninguno. Arrancaba directamente
			    con la tira de secciones, así que se llamaba «Configuración» en el menú y en
			    ninguna parte de la página — era la única de las 15 sin título.
			    El título sale de la MISMA clave que la etiqueta del menú, y distingue empleado de
			    autónomo igual que la hace el menú. Subtítulo nuevo, aprobado por Marisa el
			    29/09/26.
			    `width="full"` porque el ancho lo pone el contenedor (`max-w-2xl`).
			    `mb-6` porque `SectionNav` es `sticky` y no trae margen superior, y este contenedor
			    no tiene `space-y`: sin esto la cabecera queda a 12 px de las píldoras, mientras su
			    hermana de admin respira 24. */}
			<PageHeader
				className="mb-6"
				width="full"
				title={translate(
					isIndividual ? 'user.dashboard.individualSettings' : 'user.dashboard.userSettings'
				)}
				subtitle={translate('settings.screenSubtitle')}
			/>
			<SectionNav
				activeSection={activeSection}
				onClickSection={handleClickSection}
				translate={translate}
				sinZonaPeligro={!conZonaPeligro}
				zonaPeligroComoCuenta={exEmpleado}
			/>

			<div className="space-y-6">
				{/* 1. DATOS PERSONALES */}
				<section id="datos-personales" className="scroll-mt-28">
					<Card className="border-none shadow-xs">
						<CardContent className="p-6">
							<SectionHeader
								icon={User}
								title={translate('settings.personalData')}
								description={translate('settings.personalDataDescription')}
							/>
							<div className="space-y-4">
								{isIndividual ? (
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('settings.personalEmail')}
										</Label>
										<Input
											value={currentUser?.user?.email || ''}
											readOnly
											className="bg-muted/50"
										/>
										<p className="text-xs text-muted-foreground mt-1">
											{translate('settings.individualEmailNote')}{' '}
											<a
												href={SUPPORT_URL}
												target="_blank"
												rel="noopener noreferrer"
												className="text-primary underline"
											>
												{translate('settings.individualEmailNoteLink')}
											</a>
										</p>
									</div>
								) : (
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('settings.corporateEmail')}
										</Label>
										<Input
											value={currentUser?.user?.email || ''}
											readOnly
											className="bg-muted/50"
										/>
										<p className="text-xs text-muted-foreground mt-1">
											{translate('settings.corporateEmailNote')}
										</p>
									</div>
								)}
								<div>
									<Label className="text-sm font-medium mb-1.5 block">
										{translate('settings.phone')}
									</Label>
									<Input
										value={phone}
										onChange={(e) => setPhone(e.target.value)}
										placeholder="612 345 678"
									/>
								</div>
								{!isIndividual && (
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('settings.personalEmail')}
										</Label>
										<Input
											value={personalEmail}
											onChange={(e) => setPersonalEmail(e.target.value)}
											placeholder="tu.email@ejemplo.com"
										/>
									</div>
								)}
								<div>
									<Label className="text-sm font-medium mb-1.5 block">
										{translate('settings.profession')}
									</Label>
									<Select value={profession} onValueChange={setProfession}>
										<SelectTrigger>
											<SelectValue placeholder={translate('settings.selectProfession')} />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="tecnologia">
												{translate('settings.profession.technology')}
											</SelectItem>
											<SelectItem value="finanzas">
												{translate('settings.profession.finance')}
											</SelectItem>
											<SelectItem value="marketing">
												{translate('settings.profession.marketing')}
											</SelectItem>
											<SelectItem value="ventas">
												{translate('settings.profession.sales')}
											</SelectItem>
											<SelectItem value="rrhh">{translate('settings.profession.hr')}</SelectItem>
											<SelectItem value="otros">
												{translate('settings.profession.other')}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div>
									<Label className="text-sm font-medium mb-1.5 block">
										{translate('user.userSettings.currentLanguage')}
									</Label>
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button variant="outline" className="w-full sm:w-auto justify-between">
												{translate(`user.userSettings.${language}`)}
												<ChevronDown className="w-4 h-4 ml-2" />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent>
											{availableLanguages.map((lang) => (
												<DropdownMenuItem key={lang} onClick={() => changeLanguage(lang)}>
													{translate(`user.userSettings.${lang}`)}
												</DropdownMenuItem>
											))}
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
								<div className="flex items-center gap-3 pt-2">
									<Button
										onClick={handleSaveProfile}
										disabled={isSaving}
										className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
									>
										{isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
										{translate('settings.saveChanges')}
									</Button>
								</div>
							</div>
						</CardContent>
					</Card>
				</section>

				{/* 2. SEGURIDAD */}
				<section id="seguridad" className="scroll-mt-28">
					<Card className="border-none shadow-xs">
						<CardContent className="p-6">
							<SectionHeader
								icon={Shield}
								title={translate('settings.security')}
								description={translate('settings.securityDescription')}
							/>
							<div className="space-y-4">
								{/* Solo en la app y si el móvil tiene biometría; en la web no pinta nada. */}
								<InterruptorBiometria />
								<div>
									<Label className="text-sm font-medium mb-1.5 block">
										{translate('settings.currentPassword')}
									</Label>
									<div className="relative">
										<Input
											type={showCurrentPassword ? 'text' : 'password'}
											value={currentPassword}
											onChange={(e) => setCurrentPassword(e.target.value)}
											className="pr-10"
										/>
										<button
											type="button"
											onClick={() => setShowCurrentPassword(!showCurrentPassword)}
											className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
										>
											{showCurrentPassword ? (
												<EyeOff className="w-4 h-4" />
											) : (
												<Eye className="w-4 h-4" />
											)}
										</button>
									</div>
								</div>
								<div>
									<Label className="text-sm font-medium mb-1.5 block">
										{translate('settings.newPassword')}
									</Label>
									<div className="relative">
										<Input
											type={showNewPassword ? 'text' : 'password'}
											value={newPassword}
											onChange={(e) => setNewPassword(e.target.value)}
											placeholder={translate('settings.newPasswordPlaceholder')}
											className="pr-10"
										/>
										<button
											type="button"
											onClick={() => setShowNewPassword(!showNewPassword)}
											className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
										>
											{showNewPassword ? (
												<EyeOff className="w-4 h-4" />
											) : (
												<Eye className="w-4 h-4" />
											)}
										</button>
									</div>
								</div>
								<div>
									<Label className="text-sm font-medium mb-1.5 block">
										{translate('settings.confirmPassword')}
									</Label>
									<div className="relative">
										<Input
											type={showConfirmPassword ? 'text' : 'password'}
											value={confirmPassword}
											onChange={(e) => setConfirmPassword(e.target.value)}
											placeholder={translate('settings.confirmPasswordPlaceholder')}
											className="pr-10"
										/>
										<button
											type="button"
											onClick={() => setShowConfirmPassword(!showConfirmPassword)}
											className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
										>
											{showConfirmPassword ? (
												<EyeOff className="w-4 h-4" />
											) : (
												<Eye className="w-4 h-4" />
											)}
										</button>
									</div>
								</div>
								<Button
									onClick={handleUpdatePassword}
									disabled={isSaving || !currentPassword || !newPassword || !confirmPassword}
									className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
								>
									{isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
									{translate('settings.updatePassword')}
								</Button>
							</div>
						</CardContent>
					</Card>
				</section>

				{/* 3. RETRIBUCIÓN FLEXIBLE */}
				<section id="retribucion-flexible" className="scroll-mt-28">
					<Card className="border-none shadow-xs">
						<CardContent className="p-6">
							<SectionHeader
								icon={Coins}
								title={translate('settings.flexibleCompensation')}
								description={translate('settings.flexibleCompensationDescription')}
							/>
							<div className="rounded-lg border border-border bg-muted/30 p-5">
								<div className="flex items-start gap-3">
									<div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
										<Coins className="w-4 h-4 text-secondary" />
									</div>
									<div>
										<p className="text-sm font-medium text-foreground mb-1">
											{translate('settings.comingSoon')}
										</p>
										<p className="text-sm text-muted-foreground">
											{translate('settings.flexibleCompensationComingSoon')}
										</p>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</section>

				{/* 4. INTEGRACIONES */}
				<section id="integraciones" className="scroll-mt-28">
					<Card className="border-none shadow-xs">
						<CardContent className="p-6">
							<SectionHeader
								icon={Link2}
								title={translate('settings.integrations')}
								description={translate('settings.integrationsDescription')}
							/>
							<div className="rounded-lg border border-border p-4 flex items-center justify-between gap-4">
								<div className="flex items-center gap-3">
									<img
										src="/imgs/indexa-capital.png"
										alt="Indexa Capital"
										className="w-10 h-10 rounded-lg object-contain bg-card border border-border p-1"
									/>
									<div>
										<p className="text-sm font-semibold">Indexa Capital</p>
										<p className="text-xs text-muted-foreground">
											{translate('settings.indexaDescription')}
										</p>
									</div>
								</div>
								<Button
									onClick={() => setIsIndexaModalOpen(true)}
									variant="outline"
									size="sm"
									className={cn(
										'rounded-full shrink-0 gap-1.5',
										isIndexaConnected
											? 'border-green-600 text-green-600 hover:bg-green-50'
											: 'border-primary text-primary hover:bg-primary/5'
									)}
								>
									{isIndexaConnected ? (
										<>
											<CheckCircle2 className="w-3.5 h-3.5" />
											{translate('userIntegrations.connected')}
										</>
									) : (
										<>
											<ExternalLink className="w-3.5 h-3.5" />
											{translate('settings.connect')}
										</>
									)}
								</Button>
							</div>
							<p className="text-xs text-muted-foreground mt-3">
								{translate('settings.integrationsNote')}
							</p>
						</CardContent>
					</Card>
				</section>

				{/* 5. NOTIFICACIONES */}
				<section id="notificaciones" className="scroll-mt-28">
					<Card className="border-none shadow-xs">
						<CardContent className="p-6">
							<SectionHeader
								icon={Bell}
								title={translate('settings.notifications')}
								description={translate('settings.notificationsDescription')}
							/>
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-sm font-medium">
											{translate('settings.notifContributions')}
										</p>
										<p className="text-xs text-muted-foreground">
											{translate('settings.notifContributionsDescription')}
										</p>
									</div>
									<Switch checked={notifContributions} onCheckedChange={setNotifContributions} />
								</div>
								<Separator />
								<div className="flex items-center justify-between">
									<div>
										<p className="text-sm font-medium">{translate('settings.notifDocuments')}</p>
										<p className="text-xs text-muted-foreground">
											{translate('settings.notifDocumentsDescription')}
										</p>
									</div>
									<Switch checked={notifDocuments} onCheckedChange={setNotifDocuments} />
								</div>
								<Separator />
								<div className="flex items-center justify-between">
									<div>
										<p className="text-sm font-medium">{translate('settings.notifMarketing')}</p>
										<p className="text-xs text-muted-foreground">
											{translate('settings.notifMarketingDescription')}
										</p>
									</div>
									<Switch checked={notifMarketing} onCheckedChange={setNotifMarketing} />
								</div>
							</div>
						</CardContent>
					</Card>
				</section>

				{/* 6. DOCUMENTOS */}
				<section id="documentos" className="scroll-mt-28">
					<Card className="border-none shadow-xs">
						<CardContent className="p-6">
							<SectionHeader
								icon={FileText}
								title={translate('settings.documents')}
								description={translate('settings.documentsDescription')}
							/>
							<div className="space-y-3">
								{[
									/* {
										name: translate('settings.doc.taxCertificate'),
										desc: translate('settings.doc.taxCertificateDesc'),
										url: ''
									}, */
									{
										name: translate('settings.doc.planSpecs'),
										desc: translate('settings.doc.planSpecsDesc'),
										url:
											companyEppConfig?.linkRegulation ||
											currentUser?.userEppConfig?.linkRegulation ||
											''
									}
									/* {
										name: translate('settings.doc.adhesionBulletin'),
										desc: translate('settings.doc.adhesionBulletinDesc'),
										url: companyEppConfig?.linkAnnex || ''
									} */
								].map((doc) => (
									<div
										key={doc.name}
										className="flex items-center justify-between rounded-lg border border-border p-3.5"
									>
										<div className="flex items-center gap-3">
											<FileText className="w-5 h-5 text-muted-foreground shrink-0" />
											<div>
												<p className="text-sm font-medium">{doc.name}</p>
												<p className="text-xs text-muted-foreground">{doc.desc}</p>
											</div>
										</div>
										<Button
											variant="ghost"
											size="sm"
											className="text-primary gap-1.5 shrink-0"
											disabled={!doc.url}
											onClick={() =>
												doc.url && window.open(doc.url, '_blank', 'noopener,noreferrer')
											}
										>
											<Download className="w-4 h-4" />
											<span className="hidden sm:inline">{translate('common.download')}</span>
										</Button>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</section>

				{/* 7. SOPORTE */}
				<section id="soporte" className="scroll-mt-28">
					<Card className="border-none shadow-xs">
						<CardContent className="p-6">
							<SectionHeader
								icon={HeadphonesIcon}
								title={translate('user.settings.supportTitle')}
								description={translate('user.settings.supportNeedHelp')}
							/>
							<div className="rounded-lg border border-primary/20 bg-primary/5 p-5">
								<div className="flex items-center justify-between gap-4 flex-wrap">
									<div>
										<p className="text-sm font-semibold text-foreground">support.arcadigital.app</p>
										<p className="text-xs text-muted-foreground mt-1">
											{translate('user.settings.supportTopics')}
										</p>
									</div>
									<Button
										variant="outline"
										size="sm"
										className="rounded-full border-primary text-primary hover:bg-primary/5 gap-1.5 shrink-0"
										onClick={() => window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer')}
									>
										<Mail className="w-3.5 h-3.5" />
										{translate('user.settings.contact')}
									</Button>
								</div>
							</div>
							{/* Solo en la app; en la web no pinta nada. */}
							<EnlacePrivacidad />
						</CardContent>
					</Card>
				</section>

				{/* 8. ZONA DE PELIGRO */}
				{conZonaPeligro && (
					<section id="zona-peligro" className="scroll-mt-28">
						{/* «Tu cuenta» del ex-empleado: sin el borde rojo de peligro. */}
						<Card
							className={
								exEmpleado ? 'border-none shadow-xs' : 'border-none shadow-xs border-destructive/20'
							}
						>
							<CardContent className="p-6">
								<SectionHeader
									icon={exEmpleado ? User : AlertTriangle}
									title={translate(exEmpleado ? 'settings.yourAccount' : 'settings.dangerZone')}
									description={exEmpleado ? undefined : translate('settings.dangerZoneDescription')}
								/>
								{exEmpleado ? null : noWill ? (
									<div className="rounded-lg border border-border bg-muted/50 p-5 text-center">
										<p className="text-sm font-medium text-muted-foreground">
											{translate('settings.voluntaryExit.alreadyExited')}
										</p>
									</div>
								) : (
									<div className="rounded-lg border border-destructive/20 bg-destructive/5 p-5">
										<h3 className="text-sm font-semibold text-foreground mb-2">
											{translate('settings.voluntaryWithdrawal')}
										</h3>
										<p className="text-sm text-muted-foreground mb-4">
											{translate('settings.voluntaryWithdrawalDescription')}
										</p>
										<Button
											variant="outline"
											className="border-destructive text-destructive hover:bg-destructive/10 rounded-full"
											onClick={() => setShowExitDialog(true)}
										>
											{translate('settings.withdrawFromPlan')}
										</Button>
									</div>
								)}
								{/* Solo en la app; en la web no pinta nada. */}
								<BorrarCuenta />
							</CardContent>
						</Card>
					</section>
				)}
			</div>

			{/* Indexa Capital Config Modal */}
			<IndexaCapitalConfigModalV2
				isOpen={isIndexaModalOpen}
				onClose={() => setIsIndexaModalOpen(false)}
			/>

			{/* Voluntary Exit Multi-Step Dialog */}
			<VoluntaryExitDialogV2
				open={showExitDialog}
				onOpenChange={setShowExitDialog}
				currentUser={currentUser}
				companyEppConfig={companyEppConfig}
				token={token}
				translate={translate}
				onSuccess={() => setNoWill(true)}
			/>

			{/* Feedback Modal */}
			<FeedbackModal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				isError={modalIsError}
				message={modalMessage}
			/>
		</div>
	);
};

export default AjustesUserV2;
